// Copyright 2018 Sorama B.V.

import { Rect } from "controls/plot/IPlotPart";
import { WebGLExtensions } from "extensions/WebGLExtensions";
import BasicVertexShader from "./../../shaders/BasicVertexShader.shader";
import SpectrogramMinMaxFragmentShader from "./../../shaders/SpectrogramMinMaxFragmentShader.shader";

export class MinMaxProgram {
    private readonly gl: WebGLRenderingContext;
    private readonly maximumTextureDimension: number;

    private shaderProgram: WebGLProgram;

    private resultTexture: WebGLTexture;
    private resultFrameBuffer: WebGLFramebuffer;

    private vertexBuffer: WebGLBuffer;
    private vertexData: Float32Array;

    private pixelStorage: Uint8Array;

    // NOTE: The following origin and width/height values are in normalized units unlike the spectrogramprogram
    public xOriginLocation: WebGLUniformLocation;
    public xWidthLocation: WebGLUniformLocation;

    public yOriginLocation: WebGLUniformLocation;
    public yHeightLocation: WebGLUniformLocation;

    public textureXOffsetLocation: WebGLUniformLocation;
    public textureXScaleLocation: WebGLUniformLocation;
    public textureYScaleLocation: WebGLUniformLocation;
    //

    public dataTextureLocation: WebGLUniformLocation;
    public widthLocation: WebGLUniformLocation;

    private valueInvalid = true;
    private dataAvailable = false;
    private currentValue: { min: number; max: number };

    constructor(gl: WebGLRenderingContext, maximumTextureDimension: number) {
        this.gl = gl;
        this.maximumTextureDimension = maximumTextureDimension;

        this.initShader();
        this.initTexture();
        this.initBuffers();
    }

    private initShader(): void {
        let error: string;

        // Generate shaders
        const vertexShader = this.gl.createShader(this.gl.VERTEX_SHADER);
        const fragmentShader = this.gl.createShader(this.gl.FRAGMENT_SHADER);

        // Compile shaders
        this.gl.shaderSource(vertexShader, BasicVertexShader);
        this.gl.compileShader(vertexShader);

        if (!this.gl.getShaderParameter(vertexShader, this.gl.COMPILE_STATUS)) {
            error = this.gl.getShaderInfoLog(vertexShader);
            throw new Error(error);
        }

        this.gl.shaderSource(fragmentShader, SpectrogramMinMaxFragmentShader);
        this.gl.compileShader(fragmentShader);

        if (!this.gl.getShaderParameter(fragmentShader, this.gl.COMPILE_STATUS)) {
            error = this.gl.getShaderInfoLog(fragmentShader);
            throw new Error(error);
        }

        // Create program
        this.shaderProgram = this.gl.createProgram();
        this.gl.attachShader(this.shaderProgram, vertexShader);
        this.gl.attachShader(this.shaderProgram, fragmentShader);
        this.gl.linkProgram(this.shaderProgram);

        if (!this.gl.getProgramParameter(this.shaderProgram, this.gl.LINK_STATUS)) {
            error = this.gl.getProgramInfoLog(this.shaderProgram);
            throw new Error(error);
        }

        this.gl.useProgram(this.shaderProgram);

        this.xOriginLocation = this.gl.getUniformLocation(this.shaderProgram, "xOrigin");
        this.xWidthLocation = this.gl.getUniformLocation(this.shaderProgram, "xWidth");
        this.yOriginLocation = this.gl.getUniformLocation(this.shaderProgram, "yOrigin");
        this.yHeightLocation = this.gl.getUniformLocation(this.shaderProgram, "yHeight");
        this.textureXOffsetLocation = this.gl.getUniformLocation(this.shaderProgram, "textureXOffset");
        this.textureXScaleLocation = this.gl.getUniformLocation(this.shaderProgram, "textureXScale");
        this.textureYScaleLocation = this.gl.getUniformLocation(this.shaderProgram, "textureYScale");

        this.dataTextureLocation = this.gl.getUniformLocation(this.shaderProgram, "dataTexture");

        WebGLExtensions.ThrowOnError(this.gl);
    }

    private initTexture(): void {
        // Generate a framebuffer
        this.resultFrameBuffer = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, this.resultFrameBuffer);

        // Generate a texture
        this.resultTexture = this.gl.createTexture();
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.resultTexture);
        this.gl.texParameteri(
            WebGLRenderingContext.TEXTURE_2D,
            WebGLRenderingContext.TEXTURE_MAG_FILTER,
            WebGLRenderingContext.LINEAR
        );
        this.gl.texParameteri(
            WebGLRenderingContext.TEXTURE_2D,
            WebGLRenderingContext.TEXTURE_MIN_FILTER,
            WebGLRenderingContext.LINEAR
        );

        // Bind texture and such
        this.gl.texImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            WebGLRenderingContext.RGBA,
            this.maximumTextureDimension,
            2,
            0,
            WebGLRenderingContext.RGBA,
            WebGLRenderingContext.UNSIGNED_BYTE,
            null
        );
        this.gl.framebufferTexture2D(
            WebGLRenderingContext.FRAMEBUFFER,
            WebGLRenderingContext.COLOR_ATTACHMENT0,
            WebGLRenderingContext.TEXTURE_2D,
            this.resultTexture,
            0
        );

        // Unbind all
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, null);
        this.gl.bindRenderbuffer(WebGLRenderingContext.RENDERBUFFER, null);
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, null);

        WebGLExtensions.ThrowOnError(this.gl);
    }

    private initBuffers(): void {
        // Create a buffer and fill with data
        this.vertexBuffer = this.gl.createBuffer();
        this.vertexData = new Float32Array(8);

        this.vertexData[0] = -1;
        this.vertexData[1] = -1;

        this.vertexData[2] = -1;
        this.vertexData[3] = 1;

        this.vertexData[4] = 1;
        this.vertexData[5] = -1;

        this.vertexData[6] = 1;
        this.vertexData[7] = 1;

        this.gl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.vertexBuffer);
        this.gl.bufferData(WebGLRenderingContext.ARRAY_BUFFER, this.vertexData, WebGLRenderingContext.STATIC_DRAW);

        WebGLExtensions.ThrowOnError(this.gl);

        this.pixelStorage = new Uint8Array(2 * this.maximumTextureDimension * 4);
    }

    public updateData(
        textureIndex: number,
        validTextureXOffset: number,
        validTextureWidth: number,
        validTextureHeight: number,
        viewPort: Rect,
        viewPortBounds: Rect
    ) {
        // Bind framebuffer
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, this.resultFrameBuffer);
        this.gl.viewport(0, 0, this.maximumTextureDimension, 2);

        // Clear framebuffer
        this.gl.clearColor(0, 0, 0, 0);
        this.gl.clear(WebGLRenderingContext.COLOR_BUFFER_BIT);

        // Enable shader
        this.gl.useProgram(this.shaderProgram);

        // Update buffer data
        this.gl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.vertexBuffer);
        this.gl.bufferData(WebGLRenderingContext.ARRAY_BUFFER, this.vertexData, WebGLRenderingContext.STATIC_DRAW);
        this.gl.vertexAttribPointer(0, 2, WebGLRenderingContext.FLOAT, false, 0, 0);

        // Enable vertex arrays
        this.gl.enableVertexAttribArray(0);
        this.gl.bindAttribLocation(this.shaderProgram, 0, "vertexPosition");

        // Set shader arguments
        this.gl.uniform1i(this.dataTextureLocation, textureIndex);
        this.gl.uniform1f(this.xOriginLocation, (viewPort.x - viewPortBounds.x) / viewPortBounds.width);
        this.gl.uniform1f(this.yOriginLocation, (viewPort.y - viewPortBounds.y) / viewPortBounds.height);
        this.gl.uniform1f(this.xWidthLocation, viewPort.width / viewPortBounds.width);
        this.gl.uniform1f(this.yHeightLocation, viewPort.height / viewPortBounds.height);

        // NOTE: validTextureWidth minus one to make sure we don't sample out of bounds
        this.gl.uniform1f(this.textureXOffsetLocation, validTextureXOffset / (this.maximumTextureDimension - 1));
        this.gl.uniform1f(this.textureXScaleLocation, (validTextureWidth - 1) / (this.maximumTextureDimension - 1));
        this.gl.uniform1f(this.textureYScaleLocation, (validTextureHeight - 1) / (this.maximumTextureDimension - 1));

        this.gl.drawArrays(WebGLRenderingContext.TRIANGLE_STRIP, 0, 4);

        this.dataAvailable = true;
        this.valueInvalid = true;

        this.gl.disableVertexAttribArray(0);
        this.gl.useProgram(null);
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, null);

        this.gl.flush();
    }

    public determineMinMax(): { min: number; max: number } {
        if (!this.dataAvailable) throw new Error("No data was set for the min max program");

        if (!this.valueInvalid) {
            return { min: this.currentValue.min, max: this.currentValue.max };
        }

        // Load resulting data from texture
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, this.resultFrameBuffer);

        this.gl.readPixels(
            0,
            0,
            this.maximumTextureDimension,
            2,
            WebGLRenderingContext.RGBA,
            WebGLRenderingContext.UNSIGNED_BYTE,
            this.pixelStorage
        );

        // Disable all stuff
        this.gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, null);

        // Compute the final min max values
        let minValue = Number.POSITIVE_INFINITY;
        let maxValue = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < this.maximumTextureDimension; i++) {
            const min =
                ((<number>this.pixelStorage[i * 4]) << 24) +
                ((<number>this.pixelStorage[i * 4 + 1]) << 16) +
                ((<number>this.pixelStorage[i * 4 + 2]) << 8) +
                <number>this.pixelStorage[i * 4 + 3];

            const max =
                ((<number>this.pixelStorage[(this.maximumTextureDimension + i) * 4]) << 24) +
                ((<number>this.pixelStorage[(this.maximumTextureDimension + i) * 4 + 1]) << 16) +
                ((<number>this.pixelStorage[(this.maximumTextureDimension + i) * 4 + 2]) << 8) +
                <number>this.pixelStorage[(this.maximumTextureDimension + i) * 4 + 3];

            minValue = Math.min(minValue, min);
            maxValue = Math.max(maxValue, max);
        }

        this.valueInvalid = false;
        this.currentValue = { min: minValue, max: maxValue };
        return { min: minValue, max: maxValue };
    }
}
